<script setup lang="ts">
  import { computed } from 'vue';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import { useStableWindowSize } from '@/composition/use-stable-window-size';
  import { flowBarIsEmpty } from '@/composition/store-setup/setup-flow-screen';
  import type { IFlowBarState } from '@/composition/store-setup/setup-flow-screen';

  /**
   * The flow's single bottom bar (plan §4.0, §11.2), rendered once by the page
   * and bound to the active screen: a left action («Atrás» or the screen's
   * own), a one-line helper, an optional secondary action and the one orange
   * CTA (full width on phones). Always mounted; hidden while it has nothing
   * to show.
   */
  const PHONE_MAX_WIDTH = 768;

  const props = defineProps<{
    state: IFlowBarState;
    wide: boolean;
  }>();
  const emit = defineEmits<{
    (e: 'back'): void;
    (e: 'left'): void;
    (e: 'secondary'): void;
    (e: 'primary'): void;
  }>();
  const { width } = useStableWindowSize();

  const phone = computed(() => width.value < PHONE_MAX_WIDTH);
  const empty = computed(() => flowBarIsEmpty(props.state));
</script>

<template>
  <footer
    class="v2-setup-bottom-bar"
    :class="{ 'is-phone': phone, wide, 'is-empty': empty }"
  >
    <div class="bar-inner">
      <PosUiButton
        v-if="state.backLabel !== null"
        class="bar-back"
        color="neutral"
        variant="ghost"
        icon="arrow-left"
        :disabled="state.primaryBusy"
        @click="emit('back')"
        >{{ state.backLabel }}</PosUiButton
      >
      <PosUiButton
        v-else-if="state.leftLabel !== null"
        class="bar-left"
        color="neutral"
        variant="ghost"
        :disabled="state.primaryBusy"
        @click="emit('left')"
        >{{ state.leftLabel }}</PosUiButton
      >
      <button
        v-if="state.helper !== null && state.helperIsLink"
        type="button"
        class="bar-helper is-link"
        @click="emit('primary')"
      >
        {{ state.helper }}
      </button>
      <p v-else-if="state.helper !== null" class="bar-helper">
        {{ state.helper }}
      </p>
      <span v-else class="bar-spacer" />
      <PosUiButton
        v-if="state.secondaryLabel !== null"
        class="bar-secondary"
        color="neutral"
        variant="outline"
        :disabled="state.primaryBusy"
        @click="emit('secondary')"
        >{{ state.secondaryLabel }}</PosUiButton
      >
      <PosUiButton
        v-if="state.primaryLabel !== null"
        class="bar-primary"
        :class="{ 'is-aria-disabled': state.primaryAriaDisabled }"
        :disabled="state.primaryDisabled"
        :loading="state.primaryBusy"
        :aria-disabled="state.primaryAriaDisabled ? 'true' : undefined"
        @click="emit('primary')"
        >{{ state.primaryLabel }}</PosUiButton
      >
    </div>
  </footer>
</template>

<style scoped lang="scss">
  .v2-setup-bottom-bar {
    @apply tw-shrink-0 tw-border-0 tw-border-t tw-border-solid tw-border-neutral-200 tw-bg-white tw-px-4 tw-pt-3 md:tw-px-6;
    padding-bottom: max(12px, env(safe-area-inset-bottom));
    // Still mounted (never re-created under a focused control), just not shown.
    &.is-empty {
      @apply tw-hidden;
    }
    .bar-inner {
      @apply tw-flex tw-w-full tw-items-center tw-gap-2;
    }
    .bar-helper {
      @apply tw-m-0 tw-min-w-0 tw-flex-1 tw-truncate tw-text-right tw-text-sm tw-text-neutral-500;
      // A 44 px touch target (as tall as the buttons beside it).
      &.is-link {
        @apply tw-min-h-11 tw-cursor-pointer tw-border-0 tw-bg-transparent tw-p-0 tw-font-medium tw-text-neutral-700 tw-underline tw-underline-offset-4;
      }
    }
    .bar-spacer {
      @apply tw-flex-1;
    }
    // Long labels (German, Portuguese) wrap instead of losing words.
    .bar-primary,
    .bar-secondary {
      @apply tw-h-auto tw-min-h-11 tw-min-w-0 tw-max-w-full tw-whitespace-normal tw-py-2 tw-text-center;
    }
    // Phones: the icon says «back»; the label stays in the accessible name.
    .bar-back {
      :deep(.pos-ui-button-label) {
        @apply tw-sr-only sm:tw-not-sr-only sm:tw-inline-flex;
      }
    }
    // Phones: the helper gets its own line above the buttons (never hidden: it
    // says why the CTA is disabled) and the CTA fills the row.
    &.is-phone {
      .bar-inner {
        @apply tw-flex-wrap;
      }
      .bar-helper {
        @apply tw-order-first tw-basis-full tw-text-left tw-text-xs;
        // Its 44 px target overlaps the padding above and the gap below: the line stays compact.
        &.is-link {
          @apply tw--my-3.5;
        }
      }
      .bar-spacer {
        @apply tw-hidden;
      }
      .bar-primary {
        @apply tw-flex-1;
      }
    }
  }
</style>
